import { getEnvironmentConfig, ENDPOINTS } from '../config/apiEndpoints';

/**
 * CSRFトークン管理サービス（改修版）
 * 
 * 複数リクエスト対応:
 * - トークン取得をキュー管理してシリアライズ
 * - 未使用トークンの再利用
 * - エラー時の自動リトライ
 */
class CsrfTokenService {
  constructor() {
    this.config = getEnvironmentConfig();
    
    // トークン管理
    this.currentToken = null;
    this.tokenUsed = false;
    
    // リクエストキュー管理
    this.tokenQueue = Promise.resolve();
    
    // デバッグモード
    this.debug = process.env.NODE_ENV === 'development';
  }

  /**
   * 匿名でのCSRFトークン取得（ログイン前専用）
   * @returns {Promise<string>} CSRFトークン
   */
  async fetchAnonymousCsrfToken() {
    try {
      const csrfTokenUrl = `${this.config.baseUrl}${ENDPOINTS.CSRF.TOKEN}`;

      const response = await fetch(csrfTokenUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'CSRFトークンの取得に失敗しました');
      }
      return data.csrfToken;
    } catch (error) {
      console.error('[CSRF TOKEN SERVICE] Failed to fetch anonymous CSRF token:', error);
      throw error;
    }
  }

  /**
   * クッキーベースでCSRFトークンを取得（認証済みユーザー用）
   * 内部でキュー管理を行い、複数リクエストに対応
   * @private
   */
  async _fetchTokenFromCookie() {
    try {
      
      const url = this.config.baseUrl + ENDPOINTS.CSRF.TOKEN;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'CSRFトークンの取得に失敗しました');
      }
      return data.csrfToken;
    } catch (error) {
      console.error('[CSRF TOKEN SERVICE] Failed to fetch CSRF token from cookie:', error);
      throw error;
    }
  }

  /**
   * トークンをリセット（エラー時やログアウト時に使用）
   */
  resetToken() {
    this.currentToken = null;
    this.tokenUsed = false;
  }

  /**
   * キュー管理されたトークン取得（複数リクエスト対応）
   * 
   * 動作:
   * 1. 未使用トークンがあればそれを返す
   * 2. なければ新しいトークンを取得
   * 3. すべてのリクエストは順番に処理される
   * 
   * @returns {Promise<string>} CSRFトークン
   */
  async getTokenForRequest() {
    return this.tokenQueue = this.tokenQueue
      .then(async () => {
        // 未使用トークンがあればそれを返す
        if (this.currentToken && !this.tokenUsed) {
          this.tokenUsed = true;
          return this.currentToken;
        }

        // 新しいトークンを取得
        this.currentToken = await this._fetchTokenFromCookie();
        this.tokenUsed = true;
        return this.currentToken;
      })
      .catch(error => {
        // エラー時はトークンをリセット
        this.resetToken();
        throw error;
      });
  }

  /**
   * レガシーメソッド: generateTokenFromCookie
   * 既存コードとの互換性のために残す
   * 内部ではgetTokenForRequestを呼び出す
   * 
   * @deprecated 新しいコードではgetTokenForRequestを使用してください
   */
  async generateTokenFromCookie() {
    return this.getTokenForRequest();
  }

  /**
   * CSRFトークン付きでAPI呼び出しを実行
   * 
   * 使用例:
   * await csrfTokenService.executeWithCsrf(token => 
   *   fetch('/api/endpoint', {
   *     headers: { 'X-CSRF-TOKEN': token }
   *   })
   * );
   * 
   * @param {Function} apiCall - トークンを引数に取るAPI呼び出し関数
   * @param {number} retryCount - リトライ回数（内部使用）
   * @returns {Promise<any>} API呼び出しの結果
   */
  async executeWithCsrf(apiCall, retryCount = 0) {
    try {
      const token = await this.getTokenForRequest();
      return await apiCall(token);
    } catch (error) {
      // CSRFエラーで、まだリトライしていない場合
      if (this._isCSRFError(error) && retryCount < 1) {
        this.resetToken();
        return this.executeWithCsrf(apiCall, retryCount + 1);
      }
      throw error;
    }
  }

  /**
   * CSRFエラーかどうかを判定
   * @private
   */
  _isCSRFError(error) {
    if (!error || !error.message) return false;
    const message = error.message.toLowerCase();
    return message.includes('csrf') || 
           message.includes('セキュリティ') ||
           message.includes('トークン検証');
  }

  /**
   * ログイン時のCSRFトークン取得と送信を統合
   * @param {Function} loginApiCall - ログインAPIを呼び出す関数
   * @param {Object} credentials - ログイン資格情報
   */
  async loginWithCsrfToken(loginApiCall, credentials) {
    try {
      // ログイン前は匿名トークンを取得
      const csrfToken = await this.fetchAnonymousCsrfToken();

      // ログインAPIを呼び出す
      return await loginApiCall({
        ...credentials,
        csrfToken
      });
    } catch (error) {
      console.error('[CSRF TOKEN SERVICE] Login with CSRF token failed:', error);
      
      // CSRFトークンエラーの場合、1回だけリトライ
      if (this._isCSRFError(error)) {
        
        try {
          const newCsrfToken = await this.fetchAnonymousCsrfToken();
          return await loginApiCall({
            ...credentials,
            csrfToken: newCsrfToken
          });
        } catch (retryError) {
          console.error('[CSRF TOKEN SERVICE] Retry with new CSRF token also failed:', retryError);
          throw retryError;
        }
      }
      
      throw error;
    }
  }

  /**
   * 特定のCSRFトークンを無効化
   * @param {string} csrfToken - 無効化するCSRFトークン
   * @returns {Promise<boolean>} 無効化結果
   */
  async revokeToken(csrfToken) {
    try {
      
      if (!csrfToken) {
        throw new Error('CSRFトークンが必要です');
      }

      const url = this.config.baseUrl + ENDPOINTS.CSRF.REVOKE;
      
      const result = await this._request(url, {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': csrfToken
        }
      });

      if (result.ok && result.data && result.data.success) {
        return true;
      }

      console.warn('[CSRF TOKEN SERVICE] Failed to revoke CSRF token:', result.data?.message);
      return false;

    } catch (error) {
      console.error('[CSRF TOKEN SERVICE] Failed to revoke CSRF token:', error);
      return false;
    }
  }

  /**
   * ユーザーの全CSRFトークンを無効化
   * @returns {Promise<boolean>} 無効化結果
   */
  async revokeAllTokens() {
    try {

      const url = this.config.baseUrl + ENDPOINTS.CSRF.REVOKE_ALL;
      
      const result = await this._request(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (result.ok && result.data && result.data.success) {
        this.resetToken(); // ローカルトークンもリセット
        return true;
      }

      console.warn('[CSRF TOKEN SERVICE] Failed to revoke all CSRF tokens:', result.data?.message);
      return false;

    } catch (error) {
      console.error('[CSRF TOKEN SERVICE] Failed to revoke all CSRF tokens:', error);
      return false;
    }
  }

  /**
   * 汎用的なリクエストメソッド（エラーハンドリング付き）
   * @private
   */
  async _request(url, options) {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include'
      });

      const data = await response.json();

      return {
        ok: response.ok,
        status: response.status,
        data: data
      };
    } catch (error) {
      console.error('[CSRF TOKEN SERVICE] Request failed:', error);
      throw error;
    }
  }
}

// シングルトンインスタンスをエクスポート
export const csrfTokenService = new CsrfTokenService();

export default CsrfTokenService;